import { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import {
  ChartBar,
  CurrencyDollar,
  ArrowsClockwise,
  Waveform,
  MagnifyingGlass,
  CalendarBlank,
  CaretRight,
  CaretDown,
  Warning,
  X,
  Columns,
} from '@phosphor-icons/react';
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { Link } from '../lib/routing';
import { useUrlPagination } from '../hooks/use-url-pagination';
import { ROLE_LABEL, useRoles } from '../hooks/use-roles';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';
import { useLocalStorage } from '../hooks/use-local-storage';
import {
  fetchLlmUsage,
  fetchLlmUsageFacets,
  type LlmUsageRow,
  type LlmUsageFilter,
  type LlmUsageFacets,
} from '../lib/admin';

const HEADER_ICON = <ChartBar size={26} />;

const COLUMNS_STORAGE_KEY = 'naia:admin:llm-usage:columns-v1';
const PAGE_SIZE_STORAGE_KEY = 'naia:admin:llm-usage:page-size';

const OPTIONAL_COLUMNS = [
  { id: 'kind', label: 'Type' },
  { id: 'provider', label: 'Fournisseur' },
  { id: 'model', label: 'Modèle' },
  { id: 'feature', label: 'Fonctionnalité' },
  { id: 'subject', label: 'Rattaché à' },
  { id: 'tokens_in', label: 'Tokens in' },
  { id: 'tokens_out', label: 'Tokens out' },
  { id: 'cache_read', label: 'Cache lu' },
  { id: 'cache_create', label: 'Cache créé' },
  { id: 'thinking', label: 'Thinking' },
  { id: 'image_in', label: 'Image in' },
  { id: 'video_in', label: 'Video in' },
  { id: 'document_in', label: 'Document in' },
  { id: 'audio_in', label: 'Audio in' },
  { id: 'audio_out', label: 'Audio out' },
] as const;

type ColumnId = (typeof OPTIONAL_COLUMNS)[number]['id'];

const DEFAULT_COLUMNS: Record<ColumnId, boolean> = {
  kind: true,
  provider: true,
  model: true,
  feature: false,
  subject: true,
  tokens_in: true,
  tokens_out: true,
  cache_read: true,
  cache_create: false,
  thinking: false,
  image_in: false,
  video_in: false,
  document_in: false,
  audio_in: false,
  audio_out: false,
};

function normalizeColumns(stored: Partial<Record<ColumnId, boolean>>): Record<ColumnId, boolean> {
  return OPTIONAL_COLUMNS.reduce(
    (acc, col) => ({ ...acc, [col.id]: stored[col.id] ?? DEFAULT_COLUMNS[col.id] }),
    {} as Record<ColumnId, boolean>,
  );
}

function RelativeDate({ iso }: { iso: string }) {
  const abs = new Date(iso);
  const diffMin = Math.floor((Date.now() - abs.getTime()) / 60_000);
  let rel: string;
  if (diffMin < 1) rel = 'maintenant';
  else if (diffMin < 60) rel = `il y a ${diffMin} min`;
  else if (diffMin < 1440) rel = `il y a ${Math.floor(diffMin / 60)} h`;
  else rel = `il y a ${Math.floor(diffMin / 1440)} j`;
  return (
    <span title={abs.toLocaleString('fr-FR')} className="cursor-default">
      {rel}
    </span>
  );
}

const KIND_STYLES: Record<string, string> = {
  voice: 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]',
  text: 'bg-[var(--naia-orange-bg)] text-[var(--naia-orange)]',
  embedding: 'bg-[var(--naia-purple-bg)] text-[var(--naia-purple)]',
};

const KIND_LABELS: Record<string, string> = { text: 'Texte', voice: 'Voix', embedding: 'Embedding' };

const PROVIDER_LABELS: Record<string, string> = { vertex: 'Vertex', openrouter: 'OpenRouter', anthropic: 'Anthropic' };

const FEATURE_LABELS: Record<string, string> = {
  chat: 'Chat enseignant',
  student_chat: 'Chat élève',
  correction: 'Correction',
  rubric: 'Grille d\'évaluation',
  tutor: 'Tuteur',
  curriculum_extraction: 'Extraction de programme',
  document_title: 'Titre de document',
  bootstrap_course: 'Création de cours',
  voice: 'Voix',
  embedding: 'Embedding',
  curriculum_embedding: 'Embedding de programme',
};

function KindBadge({ kind }: { kind: 'text' | 'voice' | 'embedding' | null }) {
  if (!kind) return <span className="opacity-30">—</span>;
  return (
    <span
      className={`inline-block text-[10px] font-extrabold px-[6px] py-[2px] rounded-[4px] uppercase tracking-[0.5px] ${
        KIND_STYLES[kind] ?? 'bg-[var(--naia-orange-bg)] text-[var(--naia-orange)]'
      }`}
    >
      {KIND_LABELS[kind] ?? kind}
    </span>
  );
}

const PROVIDER_STYLES: Record<string, string> = {
  vertex: 'bg-[var(--naia-green-bg)] text-[var(--naia-green-deep)]',
  openrouter: 'bg-[var(--naia-purple-bg)] text-[var(--naia-purple)]',
  anthropic: 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]',
};

function ProviderBadge({ provider }: { provider: string | null }) {
  if (!provider) return <span className="opacity-30">—</span>;
  return (
    <span
      className={`inline-block text-[10px] font-extrabold px-[6px] py-[2px] rounded-[4px] uppercase tracking-[0.5px] ${
        PROVIDER_STYLES[provider] ?? 'bg-[var(--naia-card-alt)] text-[var(--naia-text-secondary)]'
      }`}
    >
      {PROVIDER_LABELS[provider] ?? provider}
    </span>
  );
}

/* L'entité sur laquelle la dépense a été engagée. Une ligne n'en porte qu'une :
 * le premier lien disponible gagne, du plus spécifique au plus générique. */
/* Un appel peut porter plusieurs rattachements — une correction vise une copie
   dans un devoir. N'en montrer qu'un laissait croire qu'il n'y en avait qu'un. */
function subjectLinks(row: LlmUsageRow) {
  const links: Array<{ to: string; label: string; kind: string }> = [];
  if (row.content_document_id) {
    links.push({ to: `/teacher/library/${row.content_document_id}`, label: row.content_document_title ?? 'Document', kind: 'Doc' });
  }
  if (row.assignment_id) {
    links.push({ to: `/teacher/assignments/${row.assignment_id}`, label: row.assignment_title ?? 'Devoir', kind: 'Devoir' });
  }
  if (row.exam_session_id) {
    links.push({ to: `/teacher/exams/${row.exam_session_id}`, label: row.exam_title ?? 'Copie', kind: 'Copie' });
  }
  if (row.thread_id) {
    links.push({ to: `/c/${row.thread_id}`, label: 'Conversation', kind: 'Chat' });
  }
  return links;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/* Un lot mono-document porte sa FK et n'arrive jamais ici. Les autres n'ont
   aucune entité exacte : on montre ce qui les identifie — combien de documents,
   combien de chunks — plutôt qu'un mot vague. */
function EmbedSubjectCell({ metadata }: { metadata: Record<string, unknown> | null }) {
  const query = typeof metadata?.query === 'string' ? metadata.query : null;
  if (query) {
    return (
      <span className="flex items-center gap-1.5 min-w-0" title={query}>
        <span className="shrink-0 px-1 py-[1px] rounded text-[9.5px] font-bold uppercase tracking-[0.4px] bg-[var(--naia-purple-bg)] text-[var(--naia-purple)]">
          Requête
        </span>
        <span className="truncate text-[var(--naia-text-secondary)]">{query}</span>
      </span>
    );
  }

  const docs = num(metadata?.content_document_count);
  const items = num(metadata?.item_count);
  const chunks = num(metadata?.chunk_count);
  const parts = [
    docs === null ? null : `${docs} document${docs > 1 ? 's' : ''}`,
    items === null ? null : `${items} élément${items > 1 ? 's' : ''}`,
    chunks === null ? null : `${chunks} chunk${chunks > 1 ? 's' : ''}`,
  ].filter((x): x is string => x !== null);

  if (parts.length === 0) {
    return <span className="text-[10px] uppercase tracking-[0.4px] text-[var(--naia-text-secondary)] opacity-70">corpus</span>;
  }

  const ids = Array.isArray(metadata?.content_document_ids) ? metadata.content_document_ids.join('\n') : undefined;
  return (
    <span className="flex items-center gap-1.5 min-w-0" title={ids}>
      <span className="shrink-0 px-1 py-[1px] rounded text-[9.5px] font-bold uppercase tracking-[0.4px] bg-[var(--naia-purple-bg)] text-[var(--naia-purple)]">
        Lot
      </span>
      <span className="truncate text-[var(--naia-text-secondary)]">{parts.join(' · ')}</span>
    </span>
  );
}

function SubjectCell({ row }: { row: LlmUsageRow }) {
  const links = subjectLinks(row);

  /* Un embedding n'a structurellement aucune entité. Une recherche porte au
     moins sa requête — c'est la seule chose qui explique la dépense ; une
     indexation ne porte que le corpus. Les nommer vaut mieux qu'un tiret, qui
     se lirait comme un rattachement oublié. */
  if (links.length === 0) {
    if (row.kind !== 'embedding') return <span className="opacity-30">—</span>;
    return <EmbedSubjectCell metadata={row.llm_metadata} />;
  }

  return (
    <div className="flex flex-col gap-[2px] min-w-0">
      {links.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className="flex items-center gap-1.5 min-w-0 text-[var(--naia-blue)] hover:underline"
          title={link.label}
        >
          <span className="shrink-0 px-1 py-[1px] rounded text-[9.5px] font-bold uppercase tracking-[0.4px] bg-[var(--naia-info-bg)] text-[var(--naia-text-secondary)]">
            {link.kind}
          </span>
          <span className="truncate">{link.label}</span>
        </Link>
      ))}
    </div>
  );
}

function TokenCell({ value }: { value: number }) {
  return value > 0 ? (
    <span className="tabular-nums">{value.toLocaleString('fr-FR')}</span>
  ) : (
    <span className="opacity-20">—</span>
  );
}

const TOKEN_BADGE_TONES = {
  blue: 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]',
  orange: 'bg-[var(--naia-orange-bg)] text-[var(--naia-orange)]',
  purple: 'bg-[var(--naia-purple-bg)] text-[var(--naia-purple)]',
};

function TokenBadge({ value, tone }: { value: number; tone: keyof typeof TOKEN_BADGE_TONES }) {
  if (value <= 0) return <span className="opacity-20">—</span>;
  return (
    <span
      className={`inline-block text-[10px] font-extrabold px-[5px] py-[1px] rounded-[3px] tabular-nums ${TOKEN_BADGE_TONES[tone]}`}
    >
      {value.toLocaleString('fr-FR')}
    </span>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] p-4 flex flex-col gap-1 shadow-[var(--naia-shadow-card)]">
      <div className="flex items-center gap-2 text-[var(--naia-text-secondary)]">
        <span className="text-[var(--naia-blue)]">{icon}</span>
        <span className="text-[11px] font-black uppercase tracking-[0.7px]">{label}</span>
      </div>
      <div className="text-[22px] font-extrabold text-[var(--naia-text-primary)] leading-tight">{value}</div>
      {sub && <div className="text-[11.5px] text-[var(--naia-text-secondary)]">{sub}</div>}
    </div>
  );
}

function ColumnPicker({
  columns,
  setColumns,
}: {
  columns: Record<ColumnId, boolean>;
  setColumns: (next: Record<ColumnId, boolean>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const visibleCount = OPTIONAL_COLUMNS.filter((c) => columns[c.id]).length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1.5 px-3 py-[7px] rounded-lg border text-[12px] font-semibold transition-colors ${
          open
            ? 'bg-[var(--naia-info-bg)] border-[var(--naia-blue)] text-[var(--naia-blue)]'
            : 'bg-[var(--naia-card)] border-[var(--naia-line)] text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] hover:border-[var(--naia-blue)]'
        }`}
      >
        <Columns size={14} />
        Colonnes
        <span className="ml-0.5 text-[10px] tabular-nums opacity-70">
          {visibleCount}/{OPTIONAL_COLUMNS.length}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[210px] bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
              Colonnes visibles
            </span>
            <button
              type="button"
              onClick={() => setColumns(DEFAULT_COLUMNS)}
              className="text-[10px] text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] transition-colors"
            >
              Réinitialiser
            </button>
          </div>
          <div className="flex flex-col gap-[6px] max-h-[320px] overflow-y-auto">
            {OPTIONAL_COLUMNS.map((col) => (
              <label key={col.id} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={columns[col.id]}
                  onChange={(e) => setColumns({ ...columns, [col.id]: e.target.checked })}
                  className="w-[14px] h-[14px] accent-[var(--naia-blue)] cursor-pointer"
                />
                <span className="text-[12.5px] text-[var(--naia-text-primary)] group-hover:text-[var(--naia-blue)] transition-colors">
                  {col.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TH_BASE = 'px-2 py-[7px] font-black text-[10px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase';

function Skeleton({ colCount }: { colCount: number }) {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <tr key={i} className="animate-pulse border-b border-[var(--naia-line)]">
          {Array.from({ length: colCount }).map((__, j) => (
            <td key={j} className="px-2 py-[6px]">
              <div className="h-[12px] rounded bg-[var(--naia-line)] w-[70%]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function AdminLlmUsage() {
  const { activeRole } = useRoles();
  const isAdminViewer = useIsAdminViewer();
  const { page, setPage, totalPages } = useUrlPagination();
  const [pageSize, setPageSize] = useLocalStorage<number>(PAGE_SIZE_STORAGE_KEY, 10);

  const [items, setItems] = useState<LlmUsageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCostUsd, setTotalCostUsd] = useState(0);
  const [unpricedCount, setUnpricedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState<LlmUsageFacets>({ providers: [], models: [], features: [] });

  const [storedColumns, setStoredColumns] = useLocalStorage<Partial<Record<ColumnId, boolean>>>(
    COLUMNS_STORAGE_KEY,
    DEFAULT_COLUMNS,
  );
  const columns = normalizeColumns(storedColumns);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'text' | 'voice' | 'embedding' | ''>('');
  const [provider, setProvider] = useState<LlmUsageFilter['provider'] | ''>('');
  const [model, setModel] = useState('');
  const [feature, setFeature] = useState<LlmUsageFilter['feature'] | ''>('');
  const [sinceIso, setSinceIso] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [untilIso, setUntilIso] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((filter: LlmUsageFilter, p: number, limit: number) => {
    setLoading(true);
    fetchLlmUsage({ ...filter, page: p, limit })
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
        setTotalCostUsd(r.totalCostUsd);
        setUnpricedCount(r.unpricedCount);
        const tPages = Math.max(1, Math.ceil(r.total / limit));
        if (p > tPages) setPage(1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setPage]);

  const currentFilter: LlmUsageFilter = {
    search: search.trim() || undefined,
    kind: kind || undefined,
    provider: provider || undefined,
    model: model.trim() || undefined,
    feature: feature || undefined,
    sinceIso: sinceIso ? new Date(sinceIso).toISOString() : undefined,
    untilIso: untilIso ? new Date(untilIso + 'T23:59:59').toISOString() : undefined,
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      load(currentFilter, 1, pageSize);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [search, kind, provider, model, feature, sinceIso, untilIso]);

  useEffect(() => {
    load(currentFilter, page, pageSize);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [page, pageSize]);

  useEffect(() => {
    fetchLlmUsageFacets({
      sinceIso: sinceIso ? new Date(sinceIso).toISOString() : undefined,
      untilIso: untilIso ? new Date(untilIso + 'T23:59:59').toISOString() : undefined,
    })
      .then(setFacets)
      .catch(() => {});
  }, [sinceIso, untilIso]);

  const pages = totalPages(total, pageSize);
  const hasFilters = !!(search || kind || provider || model || feature || untilIso);

  const maxCost = items.reduce((a, r) => Math.max(a, Number(r.estimated_cost_usd ?? 0)), 0);
  /* Diviser par `total` mettrait les appels non tarifes au denominateur et
     tirerait la moyenne vers le bas sans raison. */
  const pricedCount = Math.max(0, total - unpricedCount);
  const avgCost = pricedCount > 0 ? totalCostUsd / pricedCount : 0;
  const voiceCount = items.filter((r) => r.kind === 'voice').length;
  const voicePct = items.length > 0 ? Math.round((voiceCount / items.length) * 100) : 0;
  const textPct = 100 - voicePct;

  const sum = (pick: (r: LlmUsageRow) => number) => items.reduce((a, r) => a + pick(r), 0);
  const visibleColumnCount = 4 + OPTIONAL_COLUMNS.filter((c) => columns[c.id]).length;

  const resetFilters = () => {
    setSearch('');
    setKind('');
    setProvider('');
    setModel('');
    setFeature('');
    setUntilIso('');
  };

  return (
    <>
      <Header />
      <HeaderContent width="xl" showBurger backTo="/admin" backLabel="Admin" icon={HEADER_ICON}>
        <div className="flex items-center gap-2.5 min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Usage LLM</h1>
          <span className={HEADER_BADGE_CLASS}>{ROLE_LABEL[activeRole ?? 'admin']}</span>
        </div>
      </HeaderContent>
      <main className="max-w-[1400px] mx-auto px-4 pb-16 pt-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard
            icon={<ChartBar size={16} />}
            label="Total appels"
            value={loading ? '—' : total.toLocaleString('fr-FR')}
            sub="sur la période filtrée"
          />
          <KpiCard
            icon={<CurrencyDollar size={16} />}
            label="Coût total"
            value={loading ? '—' : `$${totalCostUsd.toFixed(4)}`}
            sub={
              unpricedCount > 0
                ? `USD estimé — ${unpricedCount} appel${unpricedCount > 1 ? 's' : ''} non tarifé${unpricedCount > 1 ? 's' : ''}`
                : 'USD estimé'
            }
          />
          <KpiCard
            icon={<ArrowsClockwise size={16} />}
            label="Coût moyen / appel"
            value={loading ? '—' : `$${avgCost.toFixed(4)}`}
            sub={maxCost > 0 ? `max page $${maxCost.toFixed(4)}` : undefined}
          />
          <KpiCard
            icon={<Waveform size={16} />}
            label="Texte / voix"
            value={
              loading ? (
                '—'
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[var(--naia-orange)]">{textPct}%</span>
                  <span className="text-[13px] font-normal text-[var(--naia-text-secondary)]">/</span>
                  <span className="text-[var(--naia-blue)]">{voicePct}%</span>
                </div>
              )
            }
            sub={
              <div className="mt-1 h-[6px] rounded-full overflow-hidden bg-[var(--naia-line)] flex">
                <div style={{ width: `${textPct}%` }} className="h-full bg-[var(--naia-orange)] rounded-full" />
              </div>
            }
          />
        </div>

        {/* Barre de filtres : une seule ligne qui se replie, au lieu de quatre empilées. */}
        <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] px-3 py-2 mb-3 shadow-[var(--naia-shadow-card)]">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[190px]">
              <MagnifyingGlass
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Email ou école…"
                className="pl-7 h-8 text-[12.5px] w-full"
              />
            </div>

            <NativeSelect
              value={kind}
              onChange={(e) => setKind(e.target.value as 'text' | 'voice' | 'embedding' | '')}
              className="h-8 text-[12.5px] w-[96px]"
            >
              <option value="">Type</option>
              <option value="text">{KIND_LABELS.text}</option>
              <option value="voice">{KIND_LABELS.voice}</option>
              <option value="embedding">{KIND_LABELS.embedding}</option>
            </NativeSelect>

            <NativeSelect
              value={provider}
              onChange={(e) => setProvider(e.target.value as LlmUsageFilter['provider'] | '')}
              className="h-8 text-[12.5px] w-[120px]"
            >
              <option value="">Fournisseur</option>
              {facets.providers.map((p) => (
                <option key={p} value={p}>
                  {PROVIDER_LABELS[p] ?? p}
                </option>
              ))}
            </NativeSelect>

            <NativeSelect
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="h-8 text-[12.5px] w-[150px]"
            >
              <option value="">Modèle</option>
              {facets.models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </NativeSelect>

            {facets.features.length > 0 && (
              <NativeSelect
                value={feature}
                onChange={(e) => setFeature(e.target.value as LlmUsageFilter['feature'] | '')}
                className="h-8 text-[12.5px] w-[140px]"
              >
                <option value="">Fonctionnalité</option>
                {facets.features.map((f) => (
                  <option key={f} value={f}>
                    {FEATURE_LABELS[f] ?? f}
                  </option>
                ))}
              </NativeSelect>
            )}

            <div className="flex items-center gap-1 text-[12px] text-[var(--naia-text-secondary)]">
              <CalendarBlank size={13} className="shrink-0" />
              <input
                type="date"
                value={sinceIso}
                onChange={(e) => setSinceIso(e.target.value)}
                className="h-8 border border-[var(--naia-line)] rounded-lg px-1.5 text-[12px] bg-[var(--naia-card)] text-[var(--naia-text-primary)]"
              />
              <span className="opacity-50">→</span>
              <input
                type="date"
                value={untilIso}
                onChange={(e) => setUntilIso(e.target.value)}
                className="h-8 border border-[var(--naia-line)] rounded-lg px-1.5 text-[12px] bg-[var(--naia-card)] text-[var(--naia-text-primary)]"
              />
            </div>

            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                title="Réinitialiser les filtres"
                aria-label="Réinitialiser les filtres"
                className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--naia-line)] text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] hover:border-[var(--naia-blue)] transition-colors"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <span className="text-[12px] text-[var(--naia-text-secondary)]">
            {loading ? 'Chargement…' : `${total.toLocaleString('fr-FR')} résultat${total > 1 ? 's' : ''}`}
          </span>
          <ColumnPicker columns={columns} setColumns={setStoredColumns} />
        </div>

        <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden mb-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[11.5px] whitespace-nowrap">
              <thead>
                <tr className="border-b border-[var(--naia-line)] bg-[var(--naia-card-alt)]">
                  <th className={`w-[28px] ${TH_BASE}`} />
                  <th className={`text-left ${TH_BASE}`}>Date</th>
                  <th className={`text-left ${TH_BASE}`}>Utilisateur / École</th>
                  {columns.kind && <th className={`text-left ${TH_BASE}`}>Type</th>}
                  {columns.provider && <th className={`text-left ${TH_BASE}`}>Fournisseur</th>}
                  {columns.model && <th className={`text-left ${TH_BASE}`}>Modèle</th>}
                  {columns.feature && <th className={`text-left ${TH_BASE}`}>Fonctionnalité</th>}
                  {columns.subject && <th className={`text-left ${TH_BASE}`}>Rattaché à</th>}
                  {columns.tokens_in && <th className={`text-right ${TH_BASE}`}>Tok. in</th>}
                  {columns.tokens_out && <th className={`text-right ${TH_BASE}`}>Tok. out</th>}
                  {columns.cache_read && <th className={`text-right ${TH_BASE}`}>Cache lu</th>}
                  {columns.cache_create && <th className={`text-right ${TH_BASE}`}>Cache créé</th>}
                  {columns.thinking && <th className={`text-right ${TH_BASE}`}>Thinking</th>}
                  {columns.image_in && <th className={`text-right ${TH_BASE}`}>Image in</th>}
                  {columns.video_in && <th className={`text-right ${TH_BASE}`}>Video in</th>}
                  {columns.document_in && <th className={`text-right ${TH_BASE}`}>Doc in</th>}
                  {columns.audio_in && <th className={`text-right ${TH_BASE}`}>Audio in</th>}
                  {columns.audio_out && <th className={`text-right ${TH_BASE}`}>Audio out</th>}
                  <th className={`text-right min-w-[110px] ${TH_BASE}`}>Coût USD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--naia-line)]">
                {loading ? (
                  <Skeleton colCount={visibleColumnCount} />
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="px-4 py-8 text-center text-[var(--naia-text-secondary)]">
                      Aucun résultat.
                    </td>
                  </tr>
                ) : (
                  items.map((r) => {
                    const cost = r.estimated_cost_usd === null ? null : Number(r.estimated_cost_usd);
                    const costPct = cost !== null && maxCost > 0 ? (cost / maxCost) * 100 : 0;
                    const isExpanded = expandedIds.has(r.id);
                    return (
                      <Fragment key={r.id}>
                        <tr className="hover:bg-[var(--naia-card-alt)] transition-colors">
                          <td className="px-2 py-[6px] w-[28px]">
                            {r.llm_metadata ? (
                              <button
                                type="button"
                                onClick={() => toggleExpand(r.id)}
                                aria-expanded={isExpanded}
                                aria-label="Voir les métadonnées"
                                className="text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] transition-colors w-4 h-4 flex items-center justify-center rounded hover:bg-[var(--naia-info-bg)]"
                              >
                                {isExpanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                              </button>
                            ) : (
                              <span className="inline-block w-4 h-4" />
                            )}
                          </td>

                          <td className="px-2 py-[6px] text-[var(--naia-text-secondary)]">
                            <RelativeDate iso={r.created_at} />
                          </td>

                          <td className="px-2 py-[6px] max-w-[180px]">
                            <div className="font-medium text-[var(--naia-text-primary)] truncate">
                              {r.user_email ?? (
                                r.is_system ? (
                                  <span
                                    className="text-[var(--naia-text-secondary)] italic"
                                    title="Traitement système — pas d'utilisateur à l'origine de cet appel."
                                  >
                                    système
                                  </span>
                                ) : (
                                  <span className="opacity-60 italic text-[var(--naia-text-secondary)]">
                                    {isAdminViewer ? '—' : 'Masqué (opérateur IA)'}
                                  </span>
                                )
                              )}
                            </div>
                            <div className="text-[10px] text-[var(--naia-text-secondary)] truncate">
                              {r.school_name ?? <span className="opacity-40">—</span>}
                            </div>
                          </td>

                          {columns.kind && (
                            <td className="px-2 py-[6px]">
                              <KindBadge kind={r.kind} />
                            </td>
                          )}
                          {columns.provider && (
                            <td className="px-2 py-[6px]">
                              <ProviderBadge provider={r.provider} />
                            </td>
                          )}
                          {columns.model && (
                            <td className="px-2 py-[6px] text-[var(--naia-text-secondary)] max-w-[150px]">
                              <span className="truncate block" title={r.model ?? ''}>
                                {r.model ?? <span className="opacity-30">—</span>}
                              </span>
                            </td>
                          )}
                          {columns.feature && (
                            <td className="px-2 py-[6px] text-[var(--naia-text-secondary)] max-w-[140px]">
                              <span className="truncate block" title={r.feature ?? ''}>
                                {r.feature ? (FEATURE_LABELS[r.feature] ?? r.feature) : <span className="opacity-30">—</span>}
                              </span>
                            </td>
                          )}
                          {columns.subject && (
                            <td className="px-2 py-[6px] max-w-[190px]">
                              <SubjectCell row={r} />
                            </td>
                          )}
                          {columns.tokens_in && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.input_tokens} />
                            </td>
                          )}
                          {columns.tokens_out && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.output_tokens} />
                            </td>
                          )}
                          {columns.cache_read && (
                            <td className="px-2 py-[6px] text-right">
                              <TokenBadge value={r.cache_read_input_tokens} tone="blue" />
                            </td>
                          )}
                          {columns.cache_create && (
                            <td className="px-2 py-[6px] text-right">
                              <TokenBadge value={r.cache_creation_input_tokens} tone="orange" />
                            </td>
                          )}
                          {columns.thinking && (
                            <td className="px-2 py-[6px] text-right">
                              <TokenBadge value={r.thinking_tokens} tone="purple" />
                            </td>
                          )}
                          {columns.image_in && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.image_input_tokens} />
                            </td>
                          )}
                          {columns.video_in && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.video_input_tokens} />
                            </td>
                          )}
                          {columns.document_in && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.document_input_tokens} />
                            </td>
                          )}
                          {columns.audio_in && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.audio_input_tokens} />
                            </td>
                          )}
                          {columns.audio_out && (
                            <td className="px-2 py-[6px] text-right text-[var(--naia-text-primary)]">
                              <TokenCell value={r.audio_output_tokens} />
                            </td>
                          )}

                          <td className="px-2 py-[6px] text-right relative min-w-[110px]">
                            <div
                              className="absolute inset-y-0 right-0 pointer-events-none"
                              style={{
                                width: `${costPct}%`,
                                background: 'color-mix(in srgb, var(--naia-blue) 13%, transparent)',
                              }}
                            />
                            {cost === null ? (
                              <span
                                className="relative inline-flex items-center gap-1 text-[var(--naia-orange)] font-semibold"
                                title="Aucune grille tarifaire pour ce modèle — le coût sera calculé une fois le tarif ajouté."
                              >
                                <Warning size={11} weight="fill" />
                                non tarifé
                              </span>
                            ) : (
                              <span className="relative inline-flex items-center gap-1 font-extrabold text-[var(--naia-text-primary)] tabular-nums">
                                {/* Tarif déduit d'une normalisation du nom de modèle,
                                    pas d'une correspondance exacte : à confirmer une
                                    fois, puis ajouter l'alias en base. */}
                                {r.rate_match === 'alias' && (
                                  <span
                                    className="font-normal text-[10px] px-1 rounded bg-[var(--naia-info-bg)] text-[var(--naia-text-secondary)]"
                                    title="Tarif trouvé par normalisation du nom de modèle, pas par correspondance exacte."
                                  >
                                    ~
                                  </span>
                                )}
                                ${cost.toFixed(4)}
                              </span>
                            )}
                          </td>
                        </tr>

                        {isExpanded && r.llm_metadata ? (
                          <tr>
                            <td
                              colSpan={visibleColumnCount}
                              className="px-4 py-3 bg-[var(--naia-card-alt)] border-t border-[var(--naia-line)]"
                            >
                              <div className="text-[11.5px] text-[var(--naia-text-secondary)] mb-1 font-semibold">
                                llm_metadata
                                {r.kind === 'voice' && (
                                  <span className="ml-2 opacity-60">
                                    · audio in: {r.audio_input_tokens.toLocaleString('fr-FR')} tok · audio out:{' '}
                                    {r.audio_output_tokens.toLocaleString('fr-FR')} tok
                                  </span>
                                )}
                              </div>
                              <div className="text-[12px] max-h-[280px] overflow-auto">
                                <JsonView data={r.llm_metadata} shouldExpandNode={allExpanded} style={defaultStyles} />
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
              {!loading && items.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[var(--naia-line)] bg-[var(--naia-card-alt)]">
                    <td />
                    <td
                      colSpan={2}
                      className="px-2 py-[8px] text-[10px] font-black text-[var(--naia-text-secondary)] uppercase tracking-[0.8px]"
                    >
                      Total — {total.toLocaleString('fr-FR')} appels
                      <span className="normal-case font-normal ml-1 opacity-60">(moy. ${avgCost.toFixed(4)}/appel)</span>
                    </td>
                    {columns.kind && <td />}
                    {columns.provider && <td />}
                    {columns.model && <td />}
                    {columns.feature && <td />}
                    {columns.subject && <td />}
                    {columns.tokens_in && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.tokens_out && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.output_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.cache_read && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-blue)]">
                        {sum((r) => r.cache_read_input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.cache_create && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-orange)]">
                        {sum((r) => r.cache_creation_input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.thinking && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-purple)]">
                        {sum((r) => r.thinking_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.image_in && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.image_input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.video_in && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.video_input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.document_in && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.document_input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.audio_in && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.audio_input_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    {columns.audio_out && (
                      <td className="px-2 py-[8px] tabular-nums text-right font-black text-[var(--naia-text-primary)]">
                        {sum((r) => r.audio_output_tokens).toLocaleString('fr-FR')}
                      </td>
                    )}
                    <td className="px-2 py-[8px] tabular-nums text-right font-extrabold text-[var(--naia-text-primary)]">
                      ${totalCostUsd.toFixed(4)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between text-[12.5px]">
          <span className="text-[var(--naia-text-secondary)]">
            Page {page} / {pages}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-[var(--naia-text-secondary)]">Lignes</span>
              <NativeSelect
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="text-[12px] w-[70px] min-h-[44px]"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </NativeSelect>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                Précédent
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= pages}>
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
